import { CarComparisonData, AllResults, ComparisonResults } from '../types/carComparison';
import { calculateCO2Emissions } from './co2Calculations';

// Helper function to get the calculated tax amount
export const getTaxAmount = (data: CarComparisonData) => {
  if (data.useTaxesPercent) {
    return data.totalCost * data.taxes / 100;
  } else {
    return data.taxes;
  }
};

// Helper function to calculate expected value at end
export const getExpectedValueAtEnd = (data: CarComparisonData) => {
  if (data.useDepreciationPercent) {
    // Calculate based on depreciation percentage
    const depreciationMultiplier = (100 - data.depreciationPercent) / 100;
    return (data.totalCost + getTaxAmount(data) + data.fees) * depreciationMultiplier;
  } else {
    // Use the directly entered value
    return data.expectedValueAtEnd;
  }
};

// Helper function to calculate selling costs
export const getSellingCosts = (data: CarComparisonData) => {
  if (data.useSellingCostsPercent) {
    // Calculate based on percentage of selling price (resale value)
    const resaleValue = getExpectedValueAtEnd(data);
    return resaleValue * (data.sellingCostsPercent / 100);
  } else {
    // Use the directly entered value
    return data.sellingCosts;
  }
};

// Helper function to get maintenance cost for a specific year
export const getMaintenanceCostForYear = (data: CarComparisonData, year: number) => {
  // If using simple maintenance mode, return the same annual cost for all years
  if (!data.useDetailedMaintenance) {
    return data.maintenanceCost;
  }
  
  // Otherwise, use detailed year-by-year values
  switch (year) {
    case 1: return data.maintenanceYear1;
    case 2: return data.maintenanceYear2;
    case 3: return data.maintenanceYear3;
    case 4: return data.maintenanceYear4;
    case 5: return data.maintenanceYear5;
    default: return data.maintenanceYear6Plus; // For year 6 and beyond
  }
};

// Helper function to get mileage for a specific year
export const getMileageForYear = (data: CarComparisonData, year: number) => {
  // If using simple mileage mode, return the same annual mileage for all years
  if (!data.useDetailedMileage) {
    return data.annualMileage;
  }
  
  // Otherwise, use detailed year-by-year values
  switch (year) {
    case 1: return data.mileageYear1;
    case 2: return data.mileageYear2;
    case 3: return data.mileageYear3;
    case 4: return data.mileageYear4;
    case 5: return data.mileageYear5;
    default: return data.mileageYear6Plus; // For year 6 and beyond
  }
};

export const calculateMonthlyPayment = (data: CarComparisonData) => {
  const financeFeesTaxed = data.financeFees + (data.useTaxesPercent ? data.financeFees * (data.taxes / 100) : 0);
  const principal = data.totalCost + getTaxAmount(data) + data.fees + financeFeesTaxed - data.downPayment;
  const monthlyRate = data.interestRate / 100 / 12;
  const numberOfPayments = data.financingYears * 12;

  if (numberOfPayments <= 0) return 0;

  if (monthlyRate === 0) {
    return Math.round((principal / numberOfPayments) * 100) / 100;
  }

  // Round to 2 decimal places for currency values
  return Math.round((principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1)) * 100) / 100;
};


// Calculate lease payment when APR and residual value are known
export const calculateLeasePayment = (data: CarComparisonData) => {
  // Skip if manually provided
  if (data.leasePayment && data.leaseAPR === 0) {
    return data.leasePayment;
  }

  const capCost = data.totalCost + data.fees - data.leaseDownPayment;
  const residualValue = data.residualValue;
  const months = data.leaseTerm * 12;
  if (months <= 0) return 0;
  const moneyFactor = data.leaseAPR / 2400;
  const taxRate = data.totalCost ? getTaxAmount(data) / data.totalCost : 0;

  // Base payment before tax
  const depreciationFee = (capCost - residualValue) / months;
  const financeFee = (capCost + residualValue) * moneyFactor;
  const basePayment = depreciationFee + financeFee;

  // Add monthly tax
  const monthlyPayment = basePayment * (1 + taxRate);

  return Math.round(monthlyPayment * 100) / 100;
};


// Calculate APR when lease payment and residual value are known
export const calculateLeaseAPR = (data: CarComparisonData) => {
  const capCost = data.totalCost + data.fees - data.leaseDownPayment;
  const residualValue = data.residualValue;
  const months = data.leaseTerm * 12;
  const taxRate = data.totalCost ? getTaxAmount(data) / data.totalCost : 0;
  const monthlyPayment = data.leasePayment / (1 + (taxRate ?? 0)); // remove tax before solving

  // Start with a reasonable guess
  let moneyFactor = 0.0025; // ≈ 6% APR
  const tolerance = 1e-6;
  const maxIterations = 100;

  for (let i = 0; i < maxIterations; i++) {
    const depreciationFee = (capCost - residualValue) / months;
    const financeFee = (capCost + residualValue) * moneyFactor;
    const calcPayment = depreciationFee + financeFee;
    const error = calcPayment - monthlyPayment;

    if (Math.abs(error) < tolerance) break;

    // derivative wrt moneyFactor
    const derivative = (capCost + residualValue);
    moneyFactor -= error / derivative;

    // keep reasonable
    moneyFactor = Math.max(0, Math.min(0.02, moneyFactor));
  }

  // convert MF back to APR (%)
  const leaseAPR = moneyFactor * 2400;
  return Math.round(leaseAPR * 100) / 100;
};


// Calculate residual value when lease payment and APR are known
export const calculateLeaseResidualValue = (data: CarComparisonData) => {
  const capCost = data.totalCost + data.fees - data.leaseDownPayment;
  const months = data.leaseTerm * 12;
  if (months <= 0) return 0;
  const moneyFactor = data.leaseAPR / 2400;
  const taxRate = data.totalCost ? getTaxAmount(data) / data.totalCost : 0;

  // remove monthly tax before solving
  const monthlyPayment = data.leasePayment / (1 + taxRate);

  const numerator = monthlyPayment - capCost * ((1 / months) + moneyFactor);
  const denominator = -((1 / months) - moneyFactor);
  const residualValue = numerator / denominator;

  return Math.max(0, Math.round(residualValue * 100) / 100);
};


// Helper function to calculate loan balance at any point
export const calculateLoanBalance = (data: CarComparisonData, month: number) => {
  const financeFeesTaxed = data.financeFees + (data.useTaxesPercent ? data.financeFees * (data.taxes / 100) : 0);
  const loanAmount = data.totalCost + getTaxAmount(data) + data.fees + financeFeesTaxed - data.downPayment;
  const monthlyRate = data.interestRate / 100 / 12;
  const monthlyPayment = calculateMonthlyPayment(data);

  if (month <= 0) {
    return loanAmount;
  }
  if (month >= data.financingYears * 12) {
    return 0;
  }

  // 0% interest: simple linear paydown
  if (monthlyRate === 0) {
    return Math.max(0, loanAmount - (monthlyPayment * month));
  }

  // Calculate remaining balance using amortization formula
  const remainingBalance = loanAmount * Math.pow(1 + monthlyRate, month) -
                         monthlyPayment * (Math.pow(1 + monthlyRate, month) - 1) / monthlyRate;
  return Math.max(0, remainingBalance);
};

// Calculate when loan will be paid off with extra payments
export const calculateLoanPayoffDetails = (data: CarComparisonData) => {
  const loanAmount = data.totalCost + getTaxAmount(data) + data.fees - data.downPayment;
  const monthlyRate = data.interestRate / 100 / 12;
  const basePayment = calculateMonthlyPayment(data);
  const totalPayment = basePayment + (data.extraMonthlyPayment || 0);

  let balance = loanAmount;
  let month = 0;
  let totalInterest = 0;
  const maxMonths = data.financingYears * 12;

  // Simulate month-by-month with extra payments
  while (balance > 0 && month < maxMonths) {
    month++;
    const interest = balance * monthlyRate;
    totalInterest += interest;

    const principal = Math.min(totalPayment - interest, balance);
    balance -= principal;
  }

  return {
    monthsToPayoff: month,
    yearsToPayoff: month / 12,
    totalInterest,
    interestSaved: 0 // Will calculate below
  };
};

// Helper to calculate loan balance with extra payments at any month
export const calculateLoanBalanceWithExtra = (data: CarComparisonData, targetMonth: number) => {
  const loanAmount = data.totalCost + getTaxAmount(data) + data.fees - data.downPayment;
  const monthlyRate = data.interestRate / 100 / 12;
  const basePayment = calculateMonthlyPayment(data);
  const totalPayment = basePayment + (data.extraMonthlyPayment || 0);

  let balance = loanAmount;

  for (let month = 1; month <= targetMonth; month++) {
    if (balance <= 0) return 0;

    const interest = balance * monthlyRate;
    const principal = Math.min(totalPayment - interest, balance);
    balance -= principal;
  }

  return Math.max(0, balance);
};

// Helper function to calculate fuel cost
export const calculateFuelCost = (data: CarComparisonData, mileage: number) => {
  if (data.region === 'Canada') {
    // L/100km format
    return (mileage / 100) * data.fuelEfficiency * data.fuelCostPerUnit;
  } else {
    // MPG format
    return (mileage / data.fuelEfficiency) * data.fuelCostPerUnit;
  }
};

// Helper function to apply inflation
export const applyInflation = (baseValue: number, inflationRate: number, year: number) => {
  return baseValue * Math.pow(1 + inflationRate / 100, year - 1);
};

// Helper to ensure a value is a valid number (not undefined/null/NaN)
const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined || isNaN(value)) {
    return defaultValue;
  }
  return Number(value);
};

/**
 * Complete default values for ALL numeric fields in CarComparisonData
 * Matches the initial state in CarComparisonCalculator.tsx
 *
 * MAINTAINER NOTE: If you add new numeric fields to CarComparisonData,
 * add their defaults here to prevent NaN propagation.
 */
const DEFAULT_CAR_DATA: Partial<CarComparisonData> = {
  // General Inputs
  ownershipTimeframe: 5,
  fuelEfficiency: 13.1,
  fuelCostPerUnit: 1.50,
  annualMileage: 15000,
  inflationRate: 0,

  // CO₂ Comparison
  openingMileage: 0,
  manufacturingEmissions: 10000,

  // Enhanced Mileage Inputs (years 1-5 individual, year 6+ ongoing)
  mileageYear1: 15000,
  mileageYear2: 15000,
  mileageYear3: 15000,
  mileageYear4: 15000,
  mileageYear5: 15000,
  mileageYear6Plus: 15000,

  // Buy Inputs
  totalCost: 50000,
  taxes: 13,
  fees: 0,
  cashPurchaseFees: 0,
  maintenanceCost: 2000,
  expectedValueAtEnd: 32500,
  depreciationPercent: 35,
  sellingCosts: 5000,
  sellingCostsPercent: 10,

  // Enhanced Maintenance Inputs (years 1-5 individual, year 6+ ongoing)
  maintenanceYear1: 1000,
  maintenanceYear2: 1200,
  maintenanceYear3: 1500,
  maintenanceYear4: 2000,
  maintenanceYear5: 2500,
  maintenanceYear6Plus: 3000,

  // Finance Inputs
  downPayment: 0,
  interestRate: 5.0,
  financingYears: 5,
  financeFees: 0,
  monthlyPayment: 0,
  extraMonthlyPayment: 0,

  // Lease Inputs
  leaseTerm: 4,
  totalMileageAllowance: 48000,
  costPerExtraMile: 0.25,
  leasePayment: 1000,
  residualValue: 0,
  leaseFees: 3000,
  leaseOriginationFees: 0,
  leaseAPR: 6.0,
  leaseDownPayment: 0,

  // General Costs
  annualInsurance: 3000,
  otherCarCosts: 0,

  // Opportunity Cost
  investmentReturnRate: 5.0,
};

/**
 * Sanitize all numeric fields in an object by replacing undefined/null/NaN with safe defaults
 * This automatically walks through all numeric fields defined in defaults
 *
 * @param obj - The object to sanitize
 * @param defaults - Object containing default values (only numeric fields are sanitized)
 * @returns Sanitized object with all numeric fields safe for calculations
 */
const sanitizeNumericFields = (obj: any, defaults: any): any => {
  const result = { ...obj };

  // Walk through all keys in the defaults object
  for (const key in defaults) {
    if (defaults.hasOwnProperty(key)) {
      const value = result[key];
      const defaultValue = defaults[key];

      // If default is a number, sanitize the corresponding value
      if (typeof defaultValue === 'number') {
        result[key] = safeNumber(value, defaultValue);
      }
    }
  }

  return result;
};

// eslint-disable-next-line max-lines-per-function, max-statements, complexity
export const calculateResults = (data: CarComparisonData): AllResults => {
  // Step 1: Merge incoming data with defaults (fills in missing fields)
  const mergedData = { ...DEFAULT_CAR_DATA, ...data };

  // Step 2: Sanitize all numeric fields to prevent NaN propagation
  // This automatically handles all numeric fields defined in DEFAULT_CAR_DATA
  const safeData = sanitizeNumericFields(mergedData, DEFAULT_CAR_DATA) as CarComparisonData;

  const results = {
    noCar: { finalBalance: 0, yearlyData: [] as any[] },
    buy: { finalBalance: 0, yearlyData: [] as any[] },
    finance: { finalBalance: 0, yearlyData: [] as any[] },
    lease: { finalBalance: 0, yearlyData: [] as any[] }
  };

  // Calculate No Car scenario (investment only)
  let investmentBalance = safeData.totalCost + getTaxAmount(safeData) + safeData.fees;
  for (let year = 1; year <= safeData.ownershipTimeframe; year++) {
    const openingBalance = investmentBalance;
    const investmentReturn = openingBalance * (safeData.investmentReturnRate / 100);
    investmentBalance += investmentReturn;
    
    results.noCar.yearlyData.push({
      year,
      openingBalance,
      investmentReturn,
      closingBalance: investmentBalance
    });
  }
  results.noCar.finalBalance = investmentBalance;

  // Calculate Buy scenario
  let buyBalance = 0;
  for (let year = 1; year <= safeData.ownershipTimeframe; year++) {
    const openingBalance = buyBalance;

    // Apply inflation to variable costs
    const yearMileage = getMileageForYear(safeData, year);
    const inflatedFuelCost = applyInflation(calculateFuelCost(safeData, yearMileage), safeData.inflationRate, year);
    const yearMaintenanceCost = getMaintenanceCostForYear(safeData, year);
    const inflatedMaintenanceCost = applyInflation(yearMaintenanceCost, safeData.inflationRate, year);
    const inflatedOtherCosts = applyInflation(safeData.otherCarCosts, safeData.inflationRate, year);
    const inflatedInsuranceCost = safeData.annualInsurance; // Insurance typically doesn't inflate as much

    const totalOperatingCosts = inflatedFuelCost + inflatedMaintenanceCost + inflatedOtherCosts + inflatedInsuranceCost;

    // Cash purchase only in first year (start of year transaction)
    const cashPurchaseFeesTaxed = safeData.cashPurchaseFees + (safeData.useTaxesPercent ? safeData.cashPurchaseFees * (safeData.taxes / 100) : 0);
    const cashPurchase = year === 1 ? -(safeData.totalCost + getTaxAmount(safeData) + safeData.fees + cashPurchaseFeesTaxed) : 0;

    // Investment return calculation (opportunity cost):
    // - Cash purchase happens at START of year (full year impact)
    // - Operating expenses paid throughout year (mid-year convention)
    // - Car sale happens at END of year (no impact on returns this year)
    // - Negative balance = opportunity cost (what you gave up by spending cash)
    // - Positive balance = actual returns (what you earned on leftover cash)
    const balanceAfterExpenses = openingBalance + cashPurchase - totalOperatingCosts;
    const midYearBalance = openingBalance + cashPurchase - (totalOperatingCosts / 2);
    const investmentReturn = midYearBalance * (safeData.investmentReturnRate / 100);

    // Calculate balance before sell car
    let balanceBeforeSell = balanceAfterExpenses + investmentReturn;

    // Sell car only in last year (end of year transaction)
    let sellCar = 0;
    if (year === safeData.ownershipTimeframe) {
      const inflatedResaleValue = applyInflation(getExpectedValueAtEnd(safeData), safeData.inflationRate, year);
      const inflatedSellingCosts = applyInflation(getSellingCosts(safeData), safeData.inflationRate, year);
      sellCar = inflatedResaleValue - inflatedSellingCosts;
    }
    
    buyBalance = balanceBeforeSell + sellCar;
    
    results.buy.yearlyData.push({
      year,
      openingBalance,
      cashPurchase,
      monthlyCosts: 0,
      gas: -inflatedFuelCost,
      insurance: -inflatedInsuranceCost,
      maintenance: -inflatedMaintenanceCost,
      otherCosts: -inflatedOtherCosts,
      investmentReturn,
      sellCar,
      closingBalance: buyBalance
    });
  }
  results.buy.finalBalance = buyBalance;

  // Calculate Finance scenario
  const monthlyPayment = calculateMonthlyPayment(safeData);
  const extraPayment = safeData.extraMonthlyPayment || 0;
  const totalMonthlyPayment = monthlyPayment + extraPayment;

  // Calculate when loan will be paid off (considering extra payments)
  const payoffDetails = calculateLoanPayoffDetails(safeData);
  const actualPayoffYear = Math.ceil(payoffDetails.yearsToPayoff);

  let financeBalance = 0;
  for (let year = 1; year <= safeData.ownershipTimeframe; year++) {
    const openingBalance = financeBalance;

    // Down payment in first year (start of year transaction)
    const downPayment = year === 1 ? safeData.downPayment : 0;

    // Apply inflation to variable costs
    const yearMileage = getMileageForYear(safeData, year);
    const inflatedFuelCost = applyInflation(calculateFuelCost(safeData, yearMileage), safeData.inflationRate, year);
    const yearMaintenanceCost = getMaintenanceCostForYear(safeData, year);
    const inflatedMaintenanceCost = applyInflation(yearMaintenanceCost, safeData.inflationRate, year);
    const inflatedOtherCosts = applyInflation(safeData.otherCarCosts, safeData.inflationRate, year);
    const inflatedInsuranceCost = safeData.annualInsurance;

    const totalOperatingCosts = inflatedFuelCost + inflatedMaintenanceCost + inflatedOtherCosts + inflatedInsuranceCost;

    // Monthly payments - stop after loan is paid off
    let monthlyCosts = 0;
    if (year <= actualPayoffYear) {
      // Check if loan pays off mid-year
      const monthsRemaining = payoffDetails.monthsToPayoff - ((year - 1) * 12);
      if (monthsRemaining > 0 && monthsRemaining < 12) {
        // Partial year payment
        monthlyCosts = -(totalMonthlyPayment * monthsRemaining);
      } else if (monthsRemaining >= 12) {
        // Full year payment
        monthlyCosts = -(totalMonthlyPayment * 12);
      }
    }

    // Investment return calculation (opportunity cost):
    // - Down payment happens at START of year (full year impact)
    // - Monthly payments and operating expenses paid throughout year (mid-year convention)
    // - Car sale happens at END of year (no impact on returns this year)
    // - Negative balance = opportunity cost (what you gave up by spending cash)
    // - Positive balance = actual returns (what you earned on leftover cash)
    const balanceAfterExpenses = openingBalance - downPayment + monthlyCosts - totalOperatingCosts;
    const midYearBalance = openingBalance - downPayment + (monthlyCosts / 2) - (totalOperatingCosts / 2);
    const investmentReturn = midYearBalance * (safeData.investmentReturnRate / 100);

    // Calculate balance before sell car
    let balanceBeforeSell = balanceAfterExpenses + investmentReturn;

    // Sell car only in last year (end of year transaction)
    let sellCar = 0;
    if (year === safeData.ownershipTimeframe) {
      const inflatedResaleValue = applyInflation(getExpectedValueAtEnd(safeData), safeData.inflationRate, year);
      const inflatedSellingCosts = applyInflation(getSellingCosts(safeData), safeData.inflationRate, year);

      // For finance scenario, we need to pay off the remaining loan balance
      let remainingLoanBalance = 0;
      if (year < actualPayoffYear) {
        // Calculate remaining loan balance at the time of sale (with extra payments)
        const monthsIntoLoan = year * 12;
        remainingLoanBalance = calculateLoanBalanceWithExtra(safeData, monthsIntoLoan);
      }

      // Net proceeds = Resale value - Selling costs - Remaining loan balance
      sellCar = inflatedResaleValue - inflatedSellingCosts - remainingLoanBalance;
    }

    financeBalance = balanceBeforeSell + sellCar;

    results.finance.yearlyData.push({
      year,
      openingBalance,
      downPayment: year === 1 ? -downPayment : undefined,
      monthlyCosts,
      gas: -inflatedFuelCost,
      insurance: -inflatedInsuranceCost,
      maintenance: -inflatedMaintenanceCost,
      otherCosts: -inflatedOtherCosts,
      investmentReturn,
      sellCar,
      closingBalance: financeBalance
    });
  }
  results.finance.finalBalance = financeBalance;

  // Calculate Lease scenario
  let leaseBalance = 0;

  // Track remaining principal (amount still owed on the car before residual)
  // Principal = what you need to pay down through lease payments
  const leaseOriginationFeesTaxed = safeData.leaseOriginationFees + (safeData.useTaxesPercent ? safeData.leaseOriginationFees * (safeData.taxes / 100) : 0);
  const initialPrincipal = safeData.totalCost + getTaxAmount(safeData) + safeData.fees + leaseOriginationFeesTaxed - safeData.leaseDownPayment - safeData.residualValue;
  let remainingPrincipal = initialPrincipal;

  for (let year = 1; year <= safeData.ownershipTimeframe; year++) {
    const openingBalance = leaseBalance;

    // Apply inflation to variable costs (but not lease payments)
    const yearMileage = getMileageForYear(safeData, year);
    const inflatedFuelCost = applyInflation(calculateFuelCost(safeData, yearMileage), safeData.inflationRate, year);
    const yearMaintenanceCost = getMaintenanceCostForYear(safeData, year);
    const inflatedMaintenanceCost = applyInflation(yearMaintenanceCost, safeData.inflationRate, year);
    const inflatedOtherCosts = applyInflation(safeData.otherCarCosts, safeData.inflationRate, year);
    const inflatedInsuranceCost = safeData.annualInsurance;
    
    const totalOperatingCosts = inflatedFuelCost + inflatedMaintenanceCost + inflatedOtherCosts + inflatedInsuranceCost;
    
    // Lease payments - stop when principal is paid off OR lease term ends
    let monthlyCosts = 0;
    let monthsPaid = 0;

    if (year <= safeData.leaseTerm && remainingPrincipal > 0) {
      // Calculate interest on remaining principal for this year
      const annualInterestRate = safeData.leaseAPR / 100;
      const monthlyInterestRate = annualInterestRate / 12;

      // Process each month to track principal paydown accurately
      let principalPaidThisYear = 0;
      let interestPaidThisYear = 0;

      for (let month = 1; month <= 12; month++) {
        if (remainingPrincipal <= 0) break;

        const monthlyInterest = remainingPrincipal * monthlyInterestRate;
        const monthlyPrincipal = Math.min(safeData.leasePayment - monthlyInterest, remainingPrincipal);

        remainingPrincipal -= monthlyPrincipal;
        principalPaidThisYear += monthlyPrincipal;
        interestPaidThisYear += monthlyInterest;
        monthlyCosts -= safeData.leasePayment;
        monthsPaid++;

        if (remainingPrincipal <= 0) {
          remainingPrincipal = 0; // Ensure it's exactly zero
          break;
        }
      }
    } else if (year > safeData.leaseTerm) {
      // Lease term ended, no more payments
      monthlyCosts = 0;
    }

    // Calculate kilometer overages (only during lease term, paid at end of year)
    const annualMileageAllowance = safeData.totalMileageAllowance / safeData.leaseTerm;
    const overageMiles = year <= safeData.leaseTerm ? Math.max(0, yearMileage - annualMileageAllowance) : 0;
    const overageCost = overageMiles * safeData.costPerExtraMile;

    // Apply upfront lease down payment in first year (start of year transaction)
    const leaseDownPayment = year === 1 ? safeData.leaseDownPayment : 0;

    // Investment return calculation (opportunity cost):
    // - Lease down payment happens at START of year (full year impact)
    // - Monthly payments and operating expenses paid throughout year (mid-year convention)
    // - Overage costs and car sale happen at END of year (no impact on returns this year)
    // - Negative balance = opportunity cost (what you gave up by spending cash)
    // - Positive balance = actual returns (what you earned on leftover cash)
    const balanceAfterExpenses = openingBalance - leaseDownPayment + monthlyCosts - totalOperatingCosts - overageCost;
    const midYearBalance = openingBalance - leaseDownPayment + (monthlyCosts / 2) - (totalOperatingCosts / 2);
    const investmentReturn = midYearBalance * (safeData.investmentReturnRate / 100);

    // Calculate balance before sell car
    let balanceBeforeSell = balanceAfterExpenses + investmentReturn;

    // Handle lease end scenarios
    let sellCar = 0;
    let leaseBuyoutPayment = 0;

    if (year === safeData.leaseTerm && safeData.leaseTerm < safeData.ownershipTimeframe) {
      // Lease ends before ownership - pay residual value and fees to buy the car
      leaseBuyoutPayment = -(safeData.residualValue + safeData.leaseFees);
      balanceBeforeSell += leaseBuyoutPayment;
    } else if (year === safeData.ownershipTimeframe && safeData.leaseTerm < safeData.ownershipTimeframe) {
      // Ownership ends and we own the car (lease ended earlier) - sell the car
      const inflatedResaleValue = applyInflation(getExpectedValueAtEnd(safeData), safeData.inflationRate, year);
      const inflatedSellingCosts = applyInflation(getSellingCosts(safeData), safeData.inflationRate, year);
      sellCar = inflatedResaleValue - inflatedSellingCosts;
    } else if (year === safeData.ownershipTimeframe && safeData.leaseTerm === safeData.ownershipTimeframe) {
      // Lease term equals ownership term - buy out the lease and sell the car
      leaseBuyoutPayment = -(safeData.residualValue + safeData.leaseFees);
      balanceBeforeSell += leaseBuyoutPayment;

      // Now we can sell the car since we own it
      const inflatedResaleValue = applyInflation(getExpectedValueAtEnd(safeData), safeData.inflationRate, year);
      const inflatedSellingCosts = applyInflation(getSellingCosts(safeData), safeData.inflationRate, year);
      sellCar = inflatedResaleValue - inflatedSellingCosts;
    }
    
    leaseBalance = balanceBeforeSell + sellCar;
    
    results.lease.yearlyData.push({
      year,
      openingBalance,
      monthlyCosts,
      gas: -inflatedFuelCost,
      insurance: -inflatedInsuranceCost,
      maintenance: -inflatedMaintenanceCost,
      otherCosts: -inflatedOtherCosts,
      investmentReturn,
      sellCar,
      leaseBuyoutPayment,
      overageCost,
      closingBalance: leaseBalance,
      // Debug info (optional - remove if not needed)
      remainingPrincipal,
      monthsPaid
    });
  }
  results.lease.finalBalance = leaseBalance;

  // Calculate CO₂ emissions if enabled
  const co2Results = calculateCO2Emissions(safeData);

  return {
    ...results,
    co2: co2Results
  };
};

/**
 * Calculate comparison results - DEPRECATED
 * This function now simply calls calculateResults() to ensure consistency
 * @deprecated Use calculateResults() directly instead
 */
export const calculateComparisonResults = (comparisonData: CarComparisonData): ComparisonResults => {
  // Simply delegate to calculateResults to ensure identical calculations
  return calculateResults(comparisonData);
};

// Calculate principal and interest breakdown for finance scenario
export const calculateFinancePrincipalInterest = (data: CarComparisonData) => {
  const principal = data.totalCost + getTaxAmount(data) + data.fees - data.downPayment;
  const totalPayments = calculateMonthlyPayment(data) * data.financingYears * 12;
  const interest = totalPayments - principal;
  
  return {
    principal: Math.round(principal * 100) / 100,
    interest: Math.round(interest * 100) / 100,
    totalPayments: Math.round(totalPayments * 100) / 100
  };
};

// Calculate principal and interest breakdown for lease scenario
export const calculateLeasePrincipalInterest = (data: CarComparisonData) => {
  const capitalizedCost = data.totalCost + getTaxAmount(data) + data.fees - data.leaseDownPayment;
  const principal = capitalizedCost - data.residualValue; // Depreciation portion
  const totalPayments = Math.abs((calculateResults(data).lease.yearlyData as any[]).reduce((sum: number, year: any) => sum + year.monthlyCosts, 0));  

  const interest = totalPayments - principal;
  
  return {
    principal: Math.round(principal * 100) / 100,
    interest: Math.round(interest * 100) / 100,
    totalPayments: Math.round(totalPayments * 100) / 100
  };
};